import loadable from '@loadable/component';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button, Col, Row, Table } from 'reactstrap';

import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import FullProduct from '../../../models/product/FullProduct';
import FullProductMatrix from '../../../models/product/FullProductMatrix';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import CurrencyStore from '../../../store/CurrencyStore';
import UIStore from '../../../store/UIStore';
import ProductAvailabilityType from '../../../types/ProductAvailabilityType';
import RequestState from '../../../types/RequestState';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import { calculateTotal, roundWithPrecision } from '../../../util/number';
import CustomCol from '../../bootstrap/CustomCol';
import Icon from '../../common/Icon';
import { initialImageOrigin } from '../../common/ImageLightbox/ImageLightboxProductImage';
import { DEFAULT_MAX_QUANTITY } from '../ProductAddToCart';
import ProductCollectionMatrixHeaderRow from '../ProductCollectionMatrixHeaderRow';
import ProductCollectionMatrixTableBody from '../ProductCollectionMatrixTableBody';
import ProductSizeGuides from '../ProductSizeGuides';
import {
  createAnalyticsProducts,
  updateProductSelection,
} from '../utils/matrixSelections';

const ImageLightbox = loadable(() =>
  import(
    /* webpackChunkName: "common" */ '../../common/ImageLightbox/ImageLightboxModal'
  )
);

@observer
export class ProductCollectionMatrix extends Component {
  constructor(props) {
    super(props);

    const { product } = props;

    product.sortProductImages('for_color_id');

    this.selectionUpdateTimers = {};
    this.prevMatrixSelectionRef = { current: null };

    this.state = {
      selectedProducts: [],
      analyticsProducts: [],
      mainImageIndex: null,
      lightboxIsOpen: false,
      formError: false,
      tooltipStates: {},
    };

    product.images.forEach((_, index) => {
      this.lightboxInitialState = {
        ...this.lightboxInitialState,
        [index]: {
          focus: false,
          scale: 1,
          origin: initialImageOrigin,
        },
      };
    });
  }

  getImageLightbox = () => {
    const { product } = this.props;
    const { mainImageIndex, lightboxIsOpen } = this.state;

    if (
      product.images.length === 0 ||
      !lightboxIsOpen ||
      mainImageIndex === null
    ) {
      return null;
    }

    this.lightboxInitialState.selectedImage = mainImageIndex;

    return (
      <ImageLightbox
        initialState={this.lightboxInitialState}
        mainImageIndex={mainImageIndex}
        lightboxIsOpen={lightboxIsOpen}
        product={product}
        onClick={this.toggleLightbox.bind(this, false)}
        images={product.images}
      />
    );
  };

  toggleLightbox = (lightboxIsOpen) => this.setState({ lightboxIsOpen });

  getDisplayColumnElements = () => this.props.productMatrix.getDisplayColumns();

  componentDidMount() {
    this.prevMatrixSelectionRef.current =
      this.props.cartStore.getMatrixSelection(this.props.product.id);
  }

  componentWillUnmount() {
    Object.values(this.selectionUpdateTimers).forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.selectionUpdateTimers = {};
  }

  componentDidUpdate() {
    const { cartStore, product } = this.props;
    const currentSelection = cartStore.getMatrixSelection(product.id);
    const hadProducts =
      this.prevMatrixSelectionRef.current &&
      this.prevMatrixSelectionRef.current.products?.length > 0;
    const nowEmpty =
      !currentSelection ||
      !currentSelection.products ||
      currentSelection.products.length === 0;
    if (hadProducts && nowEmpty) {
      this.clearMatrixInputs();
    }
    this.prevMatrixSelectionRef.current = currentSelection;
  }

  addSelectedProducts = () => {
    const { cartStore, analytics, accountStore, currencyStore, product } =
      this.props;
    const { selectedProducts, analyticsProducts } = this.state;
    if (!selectedProducts?.length || !analyticsProducts?.length) {
      return;
    }
    const productsAdded = selectedProducts;
    const analyticsAdded = analyticsProducts;

    cartStore.clearMatrixSelection(product.id);
    cartStore.updateProductQuantity(0);
    this.clearMatrixInputs();

    cartStore
      .populate(productsAdded, accountStore.showCollectionProductMatrix)
      .then(() => {
        const precision = 4;
        const totalValue = roundWithPrecision(
          calculateTotal(
            analyticsAdded.map((p) => p.price * p.quantity),
            precision
          ),
          precision
        );
        analytics.addToCart(analyticsAdded, totalValue);
        analytics.cartStatus({
          cart: cartStore.cart,
        });
      });
  };

  clearMatrixInputs = () => {
    const { productMatrix } = this.props;
    const clearState = {
      selectedProducts: [],
      analyticsProducts: [],
      formError: false,
    };
    productMatrix.getDisplayRows().forEach((displayRow) => {
      displayRow.cells.forEach((cell) => {
        const item = cell.item;
        if (item) {
          clearState[cell.quantityKey] = '';
          clearState[`${cell.quantityKey}--error`] = null;
        }
      });
    });
    this.setState(clearState);
  };

  calculateTotalAnalyticsValue() {
    const { analyticsProducts } = this.state;
    const precision = 4;
    return roundWithPrecision(
      calculateTotal(
        analyticsProducts.map((product) => product.price * product.quantity),
        precision
      ),
      precision
    );
  }

  renderProductMatrixTitles = () => {
    const titles = this.props.productMatrix.getNames();
    return (
      <Col>
        <div className="ProductCollectionMatrix__column-row-label">
          <span>
            {titles.columnName}
            {titles.columnName && titles.rowName && ' / '}
            {titles.rowName}
          </span>
        </div>
      </Col>
    );
  };

  setMainImageIndex = (mainImageIndex) => {
    this.setState({ mainImageIndex }, () => this.toggleLightbox(true));
  };

  handleQuantityChange = (event, productCollectionElementItem) => {
    const name = event.target.name;
    const selectedProducts = this.state.selectedProducts;
    const id = productCollectionElementItem.product.id;
    const maxQuantity = this.getMaxQuantity(id);
    const rawQty = this.getQuantity(Number(event.target.value), maxQuantity);
    const qty = rawQty === 0 ? '' : rawQty;

    this.setState({ [name]: qty }, () => {
      const quantity = this.state[name];

      this.scheduleSelectionUpdate({
        productCollectionElementItem,
        maxQuantity,
        name,
        selectedProducts,
        id,
        quantity: rawQty,
      });
    });
  };

  scheduleSelectionUpdate({
    productCollectionElementItem,
    maxQuantity,
    name,
    selectedProducts,
    id,
    quantity,
  }) {
    if (this.selectionUpdateTimers[name]) {
      clearTimeout(this.selectionUpdateTimers[name]);
    }

    this.selectionUpdateTimers[name] = setTimeout(() => {
      this.validateProductQuantity(
        productCollectionElementItem.row_id,
        productCollectionElementItem.column_id,
        maxQuantity,
        name
      );

      this.findSelectedProduct(selectedProducts, id, quantity);
      delete this.selectionUpdateTimers[name];
    }, 120);
  }

  getMaxQuantity = (activeProductId) => {
    const { accountStore, configStore, product } = this.props;

    const maxAccountQuantity =
      accountStore.account && accountStore.account.max_product_quantity > 0
        ? accountStore.account.max_product_quantity
        : DEFAULT_MAX_QUANTITY;

    const maxBackorderQuantity =
      product.availability_type === ProductAvailabilityType.ALLOW_BACKORDER
        ? configStore.product.backorderLimit > 0
          ? configStore.product.backorderLimit
          : DEFAULT_MAX_QUANTITY
        : DEFAULT_MAX_QUANTITY;

    const maxNormalProductQuantity =
      product.availability_type !== ProductAvailabilityType.ALLOW_BACKORDER
        ? product.canBeOrderedOutOfStock
          ? DEFAULT_MAX_QUANTITY
          : this.getMaxByPackage(product, activeProductId)
        : DEFAULT_MAX_QUANTITY;

    return Math.min(
      maxAccountQuantity,
      maxBackorderQuantity,
      maxNormalProductQuantity,
      DEFAULT_MAX_QUANTITY
    );
  };

  getQuantity = (qty, maxQuantity) => {
    const { product } = this.props;

    const freeQuantity = product.getFreeQuantity(product.id);

    const cannotBeOrdered =
      freeQuantity <= 0 && !product.canBeOrderedOutOfStock && qty < 0;
    const canBeOrdered = freeQuantity > 0 && qty > 0 && qty <= maxQuantity;
    const exceedsMaxQuantity = qty >= maxQuantity;
    const nonNegativeQuantity = qty > 0 ? qty : 0;

    return cannotBeOrdered
      ? 0
      : canBeOrdered
      ? nonNegativeQuantity
      : exceedsMaxQuantity
      ? maxQuantity
      : nonNegativeQuantity;
  };

  getMaxByPackage = (product, activeProductId) => {
    return product.sellInPackage
      ? Math.floor(
          product.getFreeQuantity(activeProductId) / product.package_size
        ) * product.package_size
      : product.getFreeQuantity(activeProductId);
  };

  validateProductQuantity(
    productCollectionRowElementId,
    productCollectionColumnElementId,
    maxQuantity,
    name
  ) {
    const quantityStep = this.getQuantityStep();
    const currentQuantity =
      this.state[
        `qty--${productCollectionRowElementId}--${productCollectionColumnElementId}`
      ];

    if (quantityStep > 1) {
      const validQuantity = quantityValid(maxQuantity, quantityStep);
      const validProductCount = productCountValid(quantityStep);
      const invalidQuantity =
        validQuantity(currentQuantity) || validProductCount(currentQuantity);

      if (invalidQuantity) {
        const error = { error: invalidQuantity };
        this.setState({
          [name + '--error']: error,
          formError: true,
        });
      }

      if (!invalidQuantity || !currentQuantity) {
        this.setState({
          [name + '--error']: null,
          formError: false,
        });
      }
    }
  }

  getQuantityStep = () => {
    const { product } = this.props;
    return product && product.sellInPackage ? product.package_size : 1;
  };

  findSelectedProduct = (selectedProducts, id, qty) => {
    const { accountStore, cartStore, product } = this.props;
    const withTax = accountStore.showPricesWithTax;

    // Use shared utility to update selection
    const newSelectedProducts = updateProductSelection(
      selectedProducts,
      id,
      qty
    );

    // Create analytics products
    const analyticsProducts = createAnalyticsProducts(
      newSelectedProducts,
      product,
      withTax
    );

    // Calculate total quantity for navigation row
    const totalQuantity = newSelectedProducts.reduce(
      (sum, p) => sum + p.qty,
      0
    );

    // Update local state
    this.setState({
      selectedProducts: newSelectedProducts,
      analyticsProducts,
    });

    // Persist to CartStore
    cartStore.setMatrixSelection(
      product.id,
      newSelectedProducts,
      analyticsProducts
    );

    // Sync product quantity for navigation row
    cartStore.updateProductQuantity(totalQuantity);
  };

  renderAddSelectedProductsButton = (className) => {
    const { cartStore } = this.props;
    const loading = cartStore.state === RequestState.LOADING;
    return (
      <Button
        className={className}
        onClick={this.addSelectedProducts}
        color="primary"
        disabled={this.state.formError || loading}
        block
      >
        {loading ? (
          <Icon name="circle-o-notch" spin={loading} />
        ) : (
          <FormattedMessage
            id="productCollectionMatrix.addSelectedProducts"
            defaultMessage="Add Selected Products"
          />
        )}
      </Button>
    );
  };

  render() {
    const {
      accountStore,
      configStore,
      uiStore,
      product,
      productMatrix,
      ifProductRowImages,
    } = this.props;
    const displayColumns = this.getDisplayColumnElements();
    const styles = {
      tableLayout:
        !uiStore.isMobile && displayColumns.length >= 4 ? 'fixed' : 'unset',
    };
    const showHeader =
      productMatrix.getDisplayColumns().length === 1 ||
      productMatrix.getDisplayRows().length <= 4;
    return (
      <div className="ProductCollectionMatrix">
        <Row className="ProductCollectionMatrix__info-row">
          <CustomCol>
            <p className="ProductCollectionMatrix__product-matrix-info">
              <FormattedMessage {...globalTranslations.productMatrixInfo} />
            </p>
          </CustomCol>
        </Row>
        <Row className="ProductCollectionMatrix__buttons-row">
          {!accountStore.isViewOnly && (
            <>
              <CustomCol md={3} xxl={2}>
                {this.renderAddSelectedProductsButton(
                  'ProductCollectionMatrix__info-row--add-selected-products-button'
                )}
              </CustomCol>
              <CustomCol md={9} xxl={2}>
                {product.sizeGuides.length > 0 && (
                  <ProductSizeGuides sizeGuides={product.sizeGuides} />
                )}
              </CustomCol>
            </>
          )}
        </Row>
        <Row>{this.renderProductMatrixTitles()}</Row>
        <Row>
          <Col xs={12}>
            <Table
              className="ProductCollectionMatrix__product-table"
              style={styles}
              striped={!configStore.productPage.matrixDetailedStockInfoEnabled}
            >
              {!uiStore.isMobile && showHeader && (
                <ProductCollectionMatrixHeaderRow
                  productMatrix={productMatrix}
                />
              )}
              <ProductCollectionMatrixTableBody
                product={product}
                productMatrix={productMatrix}
                ifProductRowImages={ifProductRowImages}
                tooltipStates={this.state.tooltipStates}
                onImageClick={this.setMainImageIndex}
                onQuantityChange={this.handleQuantityChange}
                getCellQuantity={(key) => this.state[key] ?? ''}
              />
            </Table>
          </Col>
        </Row>
        {!accountStore.isViewOnly && (
          <Row className="ProductCollectionMatrix__info-row-footer">
            <CustomCol
              md={{ size: 3, offset: 9 }}
              xxl={{ size: 2, offset: 10 }}
            >
              {this.renderAddSelectedProductsButton(
                'ProductCollectionMatrix__info-row-footer--add-selected-products-button'
              )}
            </CustomCol>
          </Row>
        )}
        {this.getImageLightbox()}
      </div>
    );
  }
}

ProductCollectionMatrix.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(FullProduct).isRequired,
  productMatrix: modelOf(FullProductMatrix).isRequired,
};

export default injectIntl(
  inject(
    'analytics',
    'accountStore',
    'cartStore',
    'configStore',
    'currencyStore',
    'uiStore'
  )(ProductCollectionMatrix)
);
